import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, ErrorState, TextSkeleton, cn } from '@dru/ui';
import { Download, ExternalLink } from 'lucide-react';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';

export interface DocumentOption { kind: string; label: string; available: boolean; reason?: string }

/**
 * A company's PDF, shown in a dialog, with a button for each form of it
 * (the certificate and its statutory form; the Health Check Report and
 * everything in one PDF).
 *
 * Each switch mints a fresh five-minute grant and fetches the bytes into a
 * blob URL, so the frame never depends on the API allowing itself to be
 * framed, and Download and "Open in new tab" reuse the bytes already here.
 */
export function DocumentViewer({ title, subtitle, basePath, fileName, onClose }: {
  title: string;
  subtitle?: string;
  /** `/platform/tenants/:t/certificates/:id` — `/documents` and `/documents/:kind/download` hang off it. */
  basePath: string;
  /** Saved as `<fileName>-<kind>.pdf`. */
  fileName: string;
  onClose: () => void;
}) {
  const list = useQuery<{ items: DocumentOption[] }>(() => api.get(`${basePath}/documents`), [basePath]);
  const items = list.data?.items ?? [];
  const [kind, setKind] = useState<string | null>(null);
  const [pdf, setPdf] = useState<{ kind: string; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The first form that exists, once the list arrives.
  useEffect(() => {
    if (kind === null && items.length) setKind(items.find((d) => d.available)?.kind ?? items[0]!.kind);
  }, [items, kind]);

  const current = items.find((d) => d.kind === kind);

  useEffect(() => {
    if (!current?.available) { setLoading(false); return; }
    let cancelled = false;
    let made: string | null = null;
    setLoading(true); setError(null); setPdf(null);
    (async () => {
      const grant = await api.get<{ url: string }>(`${basePath}/documents/${current.kind}/download`);
      const res = await fetch(grant.url, { credentials: 'include' });
      if (!res.ok) throw new Error('The document could not be fetched.');
      const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
      made = URL.createObjectURL(blob);
      if (cancelled) { URL.revokeObjectURL(made); return; }
      setPdf({ kind: current.kind, url: made });
    })()
      .catch((e) => { if (!cancelled) setError(errorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (made) URL.revokeObjectURL(made); };
  }, [basePath, current?.kind, current?.available]);

  function save() {
    if (!pdf) return;
    const a = document.createElement('a');
    a.href = pdf.url;
    a.download = `${fileName.replace(/[\\/]/g, '-')}-${pdf.kind}.pdf`;
    a.click();
  }

  const ready = !!pdf && pdf.kind === kind;

  return (
    <Dialog
      title={title}
      onClose={onClose}
      document
      footer={(
        <>
          <Button variant="secondary" size="sm" icon={<ExternalLink className="h-4 w-4" />} disabled={!ready}
            onClick={() => pdf && window.open(pdf.url, '_blank', 'noopener')}>
            Open in new tab
          </Button>
          <Button size="sm" icon={<Download className="h-4 w-4" />} disabled={!ready} onClick={save}>
            Download
          </Button>
        </>
      )}
    >
      <div className="flex h-full min-h-[60vh] flex-col gap-3 pt-1">
        {subtitle && <p className="font-mono text-caption text-neutral-500">{subtitle}</p>}
        {list.loading && !list.data && <TextSkeleton lines={3} />}
        {list.error && <ErrorState title="The documents could not be listed" detail={list.error} onRetry={list.reload} />}
        {items.length > 0 && (
          <div role="group" aria-label="Which document" className="flex flex-wrap gap-2">
            {items.map((d) => (
              <button
                key={d.kind}
                type="button"
                aria-pressed={d.kind === kind}
                disabled={!d.available}
                title={d.available ? d.label : d.reason}
                onClick={() => setKind(d.kind)}
                className={cn(
                  'inline-flex h-9 items-center rounded-btn border px-3 text-small transition-colors max-md:h-10',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  d.kind === kind
                    ? 'border-primary-600 bg-primary-50 font-medium text-primary-700'
                    : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        {items.filter((d) => !d.available).map((d) => (
          <p key={d.kind} className="text-caption text-neutral-600">{d.label}: {d.reason ?? 'not available yet'}</p>
        ))}
        {items.length > 0 && !items.some((d) => d.available) && (
          <Alert tone="info">Nothing can be shown for this record yet.</Alert>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
        {loading && (
          <div className="flex flex-1 items-center justify-center rounded-card border border-neutral-200 bg-neutral-50 text-small text-neutral-500">
            Preparing the PDF…
          </div>
        )}
        {ready && (
          <iframe
            title={current?.label ?? 'Document'}
            src={pdf!.url}
            className="min-h-[60vh] w-full flex-1 rounded-card border border-neutral-200 bg-white"
          />
        )}
      </div>
    </Dialog>
  );
}
